import axios, { type AxiosRequestConfig } from 'axios';
import { ApiError } from './errors';
import { attachRetryInterceptor } from './interceptors';

/**
 * Public HTTP client for unauthenticated routes — survey rendering and bulk
 * response submission. Deliberately does NOT inject an Authorization header
 * (the public survey page must work without a session, and we don't want to
 * leak a logged-in admin's token from a /s/:surveyId/:stationId link shared
 * around). Retry behaviour for transient errors is shared with the main
 * client; refresh-on-401 is intentionally absent.
 */
const BASE_URL: string = import.meta.env.VITE_API_URL ?? '';

export const publicAxios = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
});

attachRetryInterceptor(publicAxios);

publicAxios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (axios.isAxiosError(err)) throw ApiError.fromAxios(err);
    throw err;
  },
);

type Config = Omit<AxiosRequestConfig, 'method' | 'url' | 'data'>;

export const publicApi = {
  get: <T>(path: string, config?: Config) =>
    publicAxios.get<T>(path, config).then((r) => r.data),
  post: <T>(path: string, body?: unknown, config?: Config) =>
    publicAxios.post<T>(path, body, config).then((r) => r.data),
};
